import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PrepChecklist from '../components/booking/PrepChecklist';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { useAuth } from '../context/AuthContext';
import { useCreateBookingMutation, useInitiatePayuMutation } from '../store/bookingsApi';
import { useSearchTestsQuery } from '../store/edosApi';
import AddressAutofill from '../components/common/AddressAutofill';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';

// Booking — built to the v5 reference's four-step wizard, with one extra
// step the reference does not have.
//
// The reference runs Tests → Details → Time slot → Payment. "Checklist"
// sits between the slot and payment here and is NOT optional: it is the
// only place a patient is told how to prepare before a Trustian arrives,
// and a missed fast costs a real visit. Keep it.
//
// All of the real wiring is unchanged: the auth gate, the prefill from
// the signed-in user, the PayU return handling, the createBooking payload
// and the hidden-form redirect to PayU.

const STEPS = ['Tests', 'Details', 'Time slot', 'Checklist', 'Payment'];
const DONE = STEPS.length;

const HEADINGS = [
  'What would you like to test?',
  'Who is this for?',
  'When should we come?',
  'Before your test',
  'How would you like to pay?',
];

const SLOTS = [
  '6:00 – 7:00 AM', '7:00 – 8:00 AM', '8:00 – 9:00 AM', '9:00 – 10:00 AM',
  '10:00 – 11:00 AM', '12:00 – 1:00 PM', '4:00 – 5:00 PM', '6:00 – 7:00 PM',
];

const GENDERS = ['Female', 'Male', 'Other'];

const RELATIONS = ['Mother', 'Father', 'Spouse', 'Child', 'Sibling', 'Other'];

// Family members live here until there is an endpoint for them — the same
// pattern and the same defensive wrapper the cart uses, so a member added
// once is still offered on the next booking from this browser. They do NOT
// reach the server. See API-NOTES.md 2.4.
const MEMBERS_KEY = 'trustlab_members';

const readMembers = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MEMBERS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeMembers = (list) => {
  try {
    localStorage.setItem(MEMBERS_KEY, JSON.stringify(list));
  } catch {
    // Private window or quota — members just do not outlive the tab.
  }
};

const EMPTY_DRAFT = { name: '', relation: '', age: '', gender: 'Female' };

// These must match "Refunds & cancellations" in the published Terms of
// service (GET /legal/terms), which is what a customer can hold us to.
// They previously carried the v5 reference's prototype figures — a ₹150
// visit charge and a 2-4 day refund window — neither of which appears in
// our terms. API-NOTES 2.24: this table and the terms are two copies of
// one policy and should come from one source.
const CANCELLATION = [
  ['More than 2 hours before your slot', 'Free, full refund', true],
  ['Within 2 hours of your slot', '50% refund', false],
  ['After the sample is collected', 'No refund', false],
];

const PAY_BADGES = ['UPI', 'Visa', 'Mastercard', 'RuPay', 'Net banking'];

const ASSURANCES = [
  'Secure, encrypted payment',
  'Your reports are private. Shared only with you and people you choose.',
  'Free cancellation up to 2 hours before your slot',
];

const getNextDays = (n) => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < n; i += 1) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push({
      key: d.toISOString().slice(0, 10),
      day: i === 0 ? 'Today' : d.toLocaleDateString('en-IN', { weekday: 'short' }),
      date: d.getDate(),
      month: d.toLocaleDateString('en-IN', { month: 'short' }),
    });
  }
  return days;
};

// ── shared fragments ──────────────────────────────────────────────────
const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2.25,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const stepTitle = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(26px, 3.6vw, 40px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  width: '100%',
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const asideRow = { display: 'flex', justifyContent: 'space-between', gap: 1.5 };

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, totals, toggleItem, inCart, removeItem } = useCart();
  const { showToast } = useUI();
  const { user, isAuthenticated } = useAuth();
  const [createBooking, { isLoading: confirming }] = useCreateBookingMutation();
  const [initiatePayu, { isLoading: initiatingPay }] = useInitiatePayuMutation();

  const [step, setStep] = useState(0);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  // The reference collects the address as three plain fields rather than
  // through a map picker, so there is no lat/lng here any more.
  const [patient, setPatient] = useState({
    name: '', age: '', gender: 'Female', phone: '',
    street: '', area: '', pincode: '', abha: '',
    // Filled by AddressAutofill; sent as patient.location, which the
    // Booking model and bookingController already accept in full.
    location: null,
  });
  // 'me', or the id of a saved family member.
  const [person, setPerson] = useState('me');
  const [members, setMembers] = useState(readMembers);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [pay, setPay] = useState('payu');
  const [prepAcknowledged, setPrepAcknowledged] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // No auth gate on mount. The reference's "Book a test" lands straight
  // on step 1, the test picker, and that is the whole point of the
  // button — a visitor can search, add tests and see a price before being
  // asked who they are. Signing in is required to go past step 1, where
  // the flow starts asking for personal details and needs an account to
  // attach the booking to. See `next()`.

  // Prefill patient from the logged-in user.
  useEffect(() => {
    if (user && !patient.name) {
      setPatient((p) => ({
        ...p,
        name: user.name || '',
        phone: user.phone || '',
        age: user.age || '',
        gender: user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Female',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // If PayU redirected back with a failure, surface it and clear the query.
  useEffect(() => {
    const payStatus = searchParams.get('payment');
    if (!payStatus) return;
    if (payStatus === 'failed') {
      showToast(`Payment failed for ${searchParams.get('booking') || 'your booking'} — please try again`);
    } else if (payStatus === 'invalid') {
      showToast('Payment response could not be verified — contact support');
    } else if (payStatus === 'not_found') {
      showToast('Booking not found for that payment');
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 0 can add more tests without leaving the flow.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchData } = useSearchTestsQuery(
    { typewords: debounced, page: 1, perPage: 6 },
    { skip: debounced.length < 2 },
  );

  // "Myself", then every member already saved. The reference shows a
  // sample relative here; rather than invent one, the list starts with
  // just you and grows as you add people.
  const fillFrom = (src) => setPatient((pt) => ({
    ...pt,
    name: src.name || '',
    age: src.age || '',
    gender: src.gender || 'Female',
    phone: src.phone != null ? src.phone : pt.phone,
  }));

  const pickPerson = (id) => {
    setAdding(false);
    setPerson(id);
    if (id === 'me') {
      fillFrom({
        name: user?.name,
        age: user?.age,
        gender: user?.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Female',
        phone: user?.phone || '',
      });
      return;
    }
    // A member has no number of their own, so the account holder's stays
    // — the report still has somewhere to go on WhatsApp.
    const m = members.find((x) => x.id === id);
    if (m) fillFrom(m);
  };

  const startAdding = () => {
    setDraft(EMPTY_DRAFT);
    setAdding(true);
  };

  const saveMember = () => {
    if (!draft.name.trim()) {
      showToast('Give the member a name first');
      return;
    }
    const member = {
      id: `m${Date.now()}`,
      name: draft.name.trim(),
      relation: draft.relation,
      age: draft.age,
      gender: draft.gender,
    };
    const list = [...members, member];
    setMembers(list);
    writeMembers(list);
    setAdding(false);
    setPerson(member.id);
    fillFrom(member);
  };

  const removeMember = (id) => {
    const list = members.filter((x) => x.id !== id);
    setMembers(list);
    writeMembers(list);
    if (person === id) pickPerson('me');
  };

  const people = [
    {
      id: 'me',
      n: 'Myself',
      d: [user?.name, user?.age].filter(Boolean).join(', ') || 'Your profile',
      i: (user?.name || '?').trim().charAt(0).toUpperCase(),
      av: 'var(--tl-teal-l)',
    },
    ...members.map((m) => ({
      id: m.id,
      n: m.name,
      d: [m.relation, m.age].filter(Boolean).join(', ') || 'Family member',
      i: m.name.trim().charAt(0).toUpperCase(),
      av: 'var(--tl-yellow)',
      removable: true,
    })),
  ];

  // Serviceability is a front-end guess until there is an endpoint for it:
  // every pincode TrustLab currently covers is in the 5xxxxx block. See
  // API-NOTES.md — this wants a real /serviceability check.
  // A Google pick fills the three fields below it. "House / flat" keeps
  // whatever the patient already typed — Google rarely knows the flat
  // number, and overwriting it would delete the one part only they can
  // supply. Everything else is replaced, and all three stay editable.
  const applyPickedAddress = useCallback((picked) => {
    setPatient((pt) => {
      const streetLine = [picked.street_number, picked.street].filter(Boolean).join(' ');
      return {
        ...pt,
        street: pt.street?.trim() ? pt.street : streetLine,
        area: picked.sublocality || picked.area || picked.city || pt.area,
        pincode: picked.pincode || pt.pincode,
        location: {
          lat: picked.lat,
          lng: picked.lng,
          accuracy: picked.accuracy ?? null,
          place_id: picked.place_id || null,
          street_number: picked.street_number || '',
          street: picked.street || '',
          sublocality: picked.sublocality || '',
          area: picked.area || '',
          city: picked.city || '',
          district: picked.district || '',
          state: picked.state || '',
          country: picked.country || '',
          country_code: picked.country_code || '',
          pincode: picked.pincode || '',
        },
      };
    });
  }, []);

  const pinOk = /^5\d{5}$/.test(patient.pincode);
  const pinMessage = patient.pincode.length < 6
    ? 'Enter your 6-digit pincode'
    : pinOk
      ? '\u2713 Home collection available. A Trustian can reach you in about 45 minutes.'
      : 'We don\u2019t cover this pincode yet. You can visit a branch instead.';
  const pinColour = patient.pincode.length < 6
    ? 'var(--tl-muted-2)'
    : pinOk ? 'var(--tl-teal-d)' : '#B23B37';

  const days = useMemo(() => getNextDays(7), []);
  const grand = totals.subtotal;

  // Any item flagged as needing a fast steers the slot advice. Items added
  // before that flag existed simply do not set it, so the note stays off
  // rather than guessing.
  const needsFasting = items.some((i) => i.fasting);

  const selectedDay = days.find((d) => d.key === date);
  const slotSummary = selectedDay && slot
    ? `${selectedDay.day}, ${selectedDay.date} ${selectedDay.month} · ${slot}`
    : 'Not chosen yet';

  const canContinue = () => {
    if (step === 0) return items.length > 0;
    if (step === 1) return patient.name.trim() && patient.phone.length >= 10 && patient.street.trim() && pinOk;
    if (step === 2) return !!date && !!slot;
    if (step === 3) return prepAcknowledged;
    if (step === 4) return !!pay;
    return true;
  };

  const next = () => {
    // Everything past the picker needs an account: step 2 collects
    // personal details and the booking has to belong to someone.
    if (step === 0 && items.length > 0 && !isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.BOOKING)}`, { replace: false });
      return;
    }
    if (!canContinue()) {
      // A message that names what is actually missing, rather than
      // "fill all required fields" on a step with no fields.
      showToast(
        step === 0 ? 'Add a test or package to continue'
          : step === 2 ? 'Pick a date and a time slot'
            : step === 3 ? 'Please confirm you have read the preparation steps'
              : 'Please fill all required fields',
      );
      return;
    }
    if (step < STEPS.length - 1) setStep(step + 1);
    else confirm();
  };

  // Builds a hidden form and submits it so the browser navigates to PayU.
  const redirectToPayU = ({ action, method, params }) => {
    const form = document.createElement('form');
    form.method = method || 'POST';
    form.action = action;
    Object.entries(params || {}).forEach(([k, v]) => {
      const el = document.createElement('input');
      el.type = 'hidden';
      el.name = k;
      el.value = v == null ? '' : String(v);
      form.appendChild(el);
    });
    document.body.appendChild(form);
    form.submit();
  };

  async function confirm() {
    try {
      const payload = {
        items: items.map((i) => ({
          item_id: String(i.id),
          item_type: i.type,
          name: i.name,
          price: Number(i.price) || 0,
          original: i.original ?? null,
          icon: i.icon || '🧪',
          tat: i.tat || '',
          paramCount: i.paramCount || 0,
          quantity: i.quantity || 1,
        })),
        schedule: { date, slot },
        patient: {
          name: patient.name,
          age: patient.age ? Number(patient.age) : undefined,
          gender: (patient.gender || 'other').toLowerCase(),
          phone: patient.phone,
          // The pincode is folded into the address string on purpose: the
          // reference has no map picker, so there is no lat/lng to hang a
          // `location` block off, and ops still need the pincode to route
          // the visit. See API-NOTES.md.
          address: [patient.street, patient.area, patient.pincode]
            .map((x) => String(x || '').trim()).filter(Boolean).join(', '),
          // Present only when the address came from Google. The Trustian
          // app navigates on this rather than on the typed string.
          ...(patient.location ? { location: patient.location } : null),
        },
        payment_method: pay,
        discount: 0,
      };
      const res = await createBooking(payload).unwrap();
      const booking = res?.booking;
      const newId = booking?.booking_id;
      setBookingId(newId);

      // The booking is created in `awaiting_payment` and stays invisible
      // to admin until the PayU callback flips it to 'confirmed'. The
      // cart is cleared server-side once PayU verifies the transaction.
      const init = await initiatePayu({ booking_id: newId }).unwrap();
      showToast('Redirecting to PayU — do not close this tab');
      redirectToPayU(init);
    } catch (err) {
      showToast(err?.data?.message || 'Could not place the booking. Please try again.');
    }
  }

  const busy = confirming || initiatingPay;
  const isDone = step === DONE;

  // There is deliberately no "your cart is empty" screen any more: an
  // empty visit simply starts on step 1 with nothing listed, the aside
  // reads "No tests added yet" and Continue stays disabled until a test
  // is added. Sending people away to go and find tests, when this step
  // can search the catalogue itself, was the dead end.

  // ── done ──────────────────────────────────────────────────────────────
  if (isDone) {
    const nextSteps = [
      {
        n: '1',
        t: 'Preparation tips on WhatsApp',
        d: needsFasting
          ? 'We’ll remind you when to start fasting tonight.'
          : 'No fasting needed. We’ll message you the evening before.',
      },
      { n: '2', t: 'Your Trustian is assigned', d: 'You’ll see their name, photo and live location before they arrive.' },
      { n: '3', t: 'Sample collected at home', d: 'Sealed and barcoded in front of you. Takes about 10 minutes.' },
      { n: '4', t: 'Report in about 6 hours', d: 'Doctor-verified and sent to WhatsApp. A doctor calls if anything needs attention.' },
    ];
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, pt: 3, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', gap: 2,
            p: 'clamp(28px, 5vw, 44px)', borderRadius: '32px',
            bgcolor: 'var(--tl-teal)', color: '#FFFFFF',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'grid', placeItems: 'center', width: 56, height: 56,
              borderRadius: '50%', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
              fontSize: 26, fontWeight: 800,
            }}
          >
            ✓
          </Box>
          <Typography
            component="h1"
            sx={{
              m: 0, fontFamily: DISPLAY, fontWeight: 700,
              fontSize: 'clamp(32px, 5vw, 52px)', lineHeight: 0.98,
              letterSpacing: '-.03em', color: '#FFFFFF',
            }}
          >
            You&rsquo;re booked, {(patient.name || 'there').split(' ')[0]}.
          </Typography>
          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.55, color: 'var(--tl-teal-50)' }}>
            {bookingId ? `Order #${bookingId} · ` : ''}{slotSummary}. We&rsquo;ve sent the details and
            preparation tips to your WhatsApp.
          </Typography>
        </Box>

        <Box sx={{ ...card, gap: 0.5, p: 3 }}>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800, pb: 1 }}>
            What happens next
          </Box>
          {nextSteps.map((x) => (
            <Box
              key={x.n}
              sx={{
                display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 1.5,
                alignItems: 'start', py: 1.5, borderTop: '1px solid #EEF3F1',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'grid', placeItems: 'center', width: 30, height: 30,
                  borderRadius: '50%', bgcolor: 'var(--tl-mint)',
                  fontFamily: UI, fontSize: 14, fontWeight: 800, color: 'var(--tl-teal-d)',
                }}
              >
                {x.n}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>{x.t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                  {x.d}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
          {[
            { t: 'Track my booking', to: ROUTES.TRACK, dark: true },
            { t: 'Back to home', to: ROUTES.HOME, dark: false },
          ].map((b) => (
            <Box
              key={b.t}
              component={Link}
              to={b.to}
              sx={{
                p: 2, borderRadius: '16px', textAlign: 'center',
                bgcolor: b.dark ? 'var(--tl-g9)' : '#FFFFFF',
                color: b.dark ? '#FFFFFF' : 'var(--tl-g9)',
                fontFamily: UI, fontSize: 15, fontWeight: 800, textDecoration: 'none',
                '&:hover': { color: b.dark ? '#FFFFFF' : 'var(--tl-g9)' },
              }}
            >
              {b.t}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Catalogue rows for step 0: whatever the search turned up, minus
  // anything already in the visit (those are listed above it).
  const results = (searchData?.items || [])
    .filter((r) => !inCart(r.id, 'test'))
    .slice(0, 6)
    .map((r) => ({
      id: r.id,
      name: r.test_name,
      prep: /^(none|no|)$/i.test(String(r.fasting || '').trim()) ? 'No fasting' : String(r.fasting),
      price: Number(r.mrp) || 0,
      tat: r.report_tat || '',
    }));

  return (
    <Box>
      {/* ── stepper ──────────────────────────────────────────────────── */}
      <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', pt: 3, px: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))`, gap: 0.75 }}>
          {STEPS.map((label, i) => (
            <Box
              key={label}
              component="button"
              type="button"
              onClick={() => i < step && setStep(i)}
              aria-current={i === step ? 'step' : undefined}
              sx={{
                border: 0, background: 'none', p: 0, textAlign: 'left',
                cursor: i < step ? 'pointer' : 'default',
                display: 'flex', flexDirection: 'column', gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  height: 6, borderRadius: '999px',
                  bgcolor: i <= step ? 'var(--tl-teal)' : '#CFE1DB',
                  transition: `background-color .3s ${EASE}`,
                }}
              />
              <Box
                component="span"
                sx={{
                  fontFamily: UI, fontSize: 13, fontWeight: 800,
                  color: i <= step ? 'var(--tl-g9)' : '#7D918C',
                  // The ordinal alone on a phone — five labels will not fit.
                  '& .tl-sl': { display: { xs: 'none', sm: 'inline' } },
                }}
              >
                {i + 1}.<Box component="span" className="tl-sl">&nbsp;{label}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ width: '100%', maxWidth: 1200, mx: 'auto', pt: 2.5, px: 2, pb: 9 }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
          {/* ── left column ──────────────────────────────────────────── */}
          <Box sx={{ flex: '2 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* 0 — tests */}
            {step === 0 && (
              <Box component="section" sx={card}>
                <Typography component="h1" sx={stepTitle}>{HEADINGS[0]}</Typography>

                <Box
                  component="input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search tests or packages"
                  aria-label="Search tests or packages"
                  sx={field}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {items.map((it) => (
                    <Box
                      key={`${it.type}-${it.id}`}
                      sx={{
                        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1.5,
                        alignItems: 'center', py: 1.75, borderTop: '1px solid #EEF3F1',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>{it.name}</Box>
                          <Box
                            component="span"
                            sx={{
                              px: 1, py: '3px', borderRadius: '999px',
                              bgcolor: it.type === 'package' ? 'var(--tl-yellow)' : 'var(--tl-mint)',
                              color: it.type === 'package' ? 'var(--tl-g9)' : 'var(--tl-teal-d)',
                              fontFamily: UI, fontSize: 11, fontWeight: 800, textTransform: 'capitalize',
                            }}
                          >
                            {it.type}
                          </Box>
                        </Box>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                          {[it.tat, Number(it.price) > 0 ? `₹ ${Number(it.price).toLocaleString('en-IN')}` : null]
                            .filter(Boolean).join(' · ')}
                        </Box>
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => removeItem(it.id, it.type)}
                        sx={{
                          border: 0, cursor: 'pointer', borderRadius: '999px',
                          p: '10px 16px', minHeight: 40, whiteSpace: 'nowrap',
                          bgcolor: 'var(--tl-teal)', color: '#FFFFFF',
                          fontFamily: UI, fontSize: 14, fontWeight: 800,
                        }}
                      >
                        Added ✓
                      </Box>
                    </Box>
                  ))}

                  {results.map((r) => (
                    <Box
                      key={`res-${r.id}`}
                      sx={{
                        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1.5,
                        alignItems: 'center', py: 1.75, borderTop: '1px solid #EEF3F1',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>{r.name}</Box>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                          {[r.prep, Number(r.price) > 0 ? `₹ ${r.price.toLocaleString('en-IN')}` : null]
                            .filter(Boolean).join(' · ')}
                        </Box>
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => toggleItem({ id: r.id, type: 'test', name: r.name, price: r.price, tat: r.tat })}
                        sx={{
                          border: 0, cursor: 'pointer', borderRadius: '999px',
                          p: '10px 16px', minHeight: 40, whiteSpace: 'nowrap',
                          bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)',
                          fontFamily: UI, fontSize: 14, fontWeight: 800,
                        }}
                      >
                        + Add
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                    justifyContent: 'space-between', gap: 2,
                    p: '18px 20px', borderRadius: '20px', border: '2px dashed #B9DCD3',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>
                      Have a prescription?
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                      Upload it and we&rsquo;ll add the right tests for you.
                    </Box>
                  </Box>
                  <Box
                    component={Link}
                    to={ROUTES.SUPPORT}
                    sx={{
                      p: '10px 16px', borderRadius: '999px', bgcolor: 'var(--tl-mint)',
                      color: 'var(--tl-g9)', fontFamily: UI, fontSize: 14, fontWeight: 800,
                      textDecoration: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    Upload photo
                  </Box>
                </Box>
              </Box>
            )}

            {/* 1 — details. Built to the reference exactly: the person
                   picker, four identity fields, three plain address fields
                   and the ABHA block. This replaced the Google-Maps
                   LocationPicker the old flow used — see the note on
                   `address` in confirm() for what that costs. */}
            {step === 1 && (
              <Box component="section" sx={card}>
                <Typography component="h1" sx={stepTitle}>{HEADINGS[1]}</Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {people.map((pr) => {
                    const on = !adding && pr.id === person;
                    return (
                      <Box
                        key={pr.id}
                        sx={{
                          position: 'relative',
                          // Room for the remove button so it never sits on
                          // top of the member's name.
                          pr: pr.removable ? '10px' : 0,
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => pickPerson(pr.id)}
                          aria-pressed={on}
                          sx={{
                            cursor: 'pointer', color: 'var(--tl-g9)',
                            display: 'flex', alignItems: 'center', gap: 1.25,
                            p: '12px 16px', borderRadius: '18px',
                            border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                            bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'grid', placeItems: 'center', width: 34, height: 34,
                              flexShrink: 0, borderRadius: '50%', bgcolor: pr.av,
                              fontFamily: UI, fontSize: 14, fontWeight: 800,
                            }}
                          >
                            {pr.i}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>{pr.n}</Box>
                            <Box component="span" sx={{ fontFamily: UI, fontSize: 12, color: 'var(--tl-muted-2)' }}>{pr.d}</Box>
                          </Box>
                        </Box>
                        {pr.removable && (
                          <Box
                            component="button"
                            type="button"
                            onClick={() => removeMember(pr.id)}
                            aria-label={`Remove ${pr.n}`}
                            title={`Remove ${pr.n}`}
                            sx={{
                              position: 'absolute', top: -6, right: -2,
                              width: 22, height: 22, borderRadius: '50%',
                              border: '1.5px solid var(--tl-line)', bgcolor: '#FFFFFF',
                              color: 'var(--tl-muted-2)', cursor: 'pointer', p: 0,
                              fontFamily: UI, fontSize: 13, fontWeight: 800, lineHeight: 1,
                              '&:hover': { borderColor: '#B23B37', color: '#B23B37' },
                            }}
                          >
                            ×
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                  {/* Opens the create form below rather than silently
                      blanking the fields, which gave no sign that a new
                      person was being entered. */}
                  <Box
                    component="button"
                    type="button"
                    onClick={startAdding}
                    aria-expanded={adding}
                    sx={{
                      cursor: 'pointer', color: 'var(--tl-g9)',
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      p: '12px 16px', borderRadius: '18px',
                      border: `1.5px ${adding ? 'solid var(--tl-teal)' : 'dashed var(--tl-line)'}`,
                      bgcolor: adding ? 'var(--tl-mint)' : '#FFFFFF',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'grid', placeItems: 'center', width: 34, height: 34,
                        flexShrink: 0, borderRadius: '50%', bgcolor: 'var(--tl-mint)',
                        fontFamily: UI, fontSize: 18, fontWeight: 800,
                      }}
                    >
                      +
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>Someone else</Box>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 12, color: 'var(--tl-muted-2)' }}>
                        At the same address
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* ── create a family member ──────────────────────────── */}
                {adding && (
                  <Box
                    sx={{
                      display: 'flex', flexDirection: 'column', gap: 1.75,
                      p: 'clamp(16px, 2.4vw, 22px)', borderRadius: '20px',
                      bgcolor: '#F6FAF8', border: '1.5px solid var(--tl-line)',
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>
                      Add a family member
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                      <Box component="label" sx={fieldLabel}>
                        Full name
                        <Box
                          component="input"
                          value={draft.name}
                          autoFocus
                          placeholder="e.g. Lakshmi Reddy"
                          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                          sx={{ ...field, bgcolor: '#FFFFFF' }}
                        />
                      </Box>
                      <Box component="label" sx={fieldLabel}>
                        Age
                        <Box
                          component="input"
                          value={draft.age}
                          inputMode="numeric"
                          onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
                          sx={{ ...field, bgcolor: '#FFFFFF' }}
                        />
                      </Box>
                    </Box>

                    <Box sx={fieldLabel}>
                      Gender
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {GENDERS.map((g) => {
                          const on = draft.gender === g;
                          return (
                            <Box
                              key={g}
                              component="button"
                              type="button"
                              onClick={() => setDraft((d) => ({ ...d, gender: g }))}
                              aria-pressed={on}
                              sx={{
                                flex: 1, cursor: 'pointer', borderRadius: '14px',
                                p: '12px 6px', fontFamily: UI, fontSize: 14, fontWeight: 800,
                                color: 'var(--tl-g9)',
                                border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                                bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                              }}
                            >
                              {g}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box sx={fieldLabel}>
                      Relationship (optional)
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {RELATIONS.map((r) => {
                          const on = draft.relation === r;
                          return (
                            <Box
                              key={r}
                              component="button"
                              type="button"
                              onClick={() => setDraft((d) => ({ ...d, relation: on ? '' : r }))}
                              aria-pressed={on}
                              sx={{
                                cursor: 'pointer', borderRadius: '999px',
                                p: '9px 14px', fontFamily: UI, fontSize: 14, fontWeight: 700,
                                color: 'var(--tl-g9)',
                                border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                                bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                              }}
                            >
                              {r}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={saveMember}
                        sx={{
                          border: 0, cursor: 'pointer', borderRadius: '14px',
                          p: '13px 20px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
                          fontFamily: UI, fontSize: 15, fontWeight: 800,
                        }}
                      >
                        Save member
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setAdding(false)}
                        sx={{
                          cursor: 'pointer', borderRadius: '14px', p: '13px 20px',
                          border: '1.5px solid var(--tl-line)', bgcolor: 'transparent',
                          color: 'var(--tl-g9)', fontFamily: UI, fontSize: 15, fontWeight: 800,
                        }}
                      >
                        Cancel
                      </Box>
                    </Box>

                    <Box component="span" sx={{ fontFamily: UI, fontSize: 12.5, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                      Saved on this device for now. Members will move to your
                      account once family profiles are live.
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                  <Box component="label" sx={fieldLabel}>
                    Full name
                    <Box
                      component="input"
                      value={patient.name}
                      onChange={(e) => setPatient((pt) => ({ ...pt, name: e.target.value }))}
                      sx={field}
                    />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Mobile (WhatsApp)
                    <Box
                      component="input"
                      value={patient.phone}
                      inputMode="tel"
                      onChange={(e) => setPatient((pt) => ({ ...pt, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      sx={field}
                    />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Age
                    <Box
                      component="input"
                      value={patient.age}
                      inputMode="numeric"
                      onChange={(e) => setPatient((pt) => ({ ...pt, age: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
                      sx={field}
                    />
                  </Box>
                  <Box sx={fieldLabel}>
                    Gender
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {GENDERS.map((g) => {
                        const on = patient.gender === g;
                        return (
                          <Box
                            key={g}
                            component="button"
                            type="button"
                            onClick={() => setPatient((pt) => ({ ...pt, gender: g }))}
                            aria-pressed={on}
                            sx={{
                              flex: 1, cursor: 'pointer', borderRadius: '14px',
                              p: '12px 6px', fontFamily: UI, fontSize: 14, fontWeight: 800,
                              color: 'var(--tl-g9)',
                              border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                              bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                            }}
                          >
                            {g}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>

                <Typography
                  component="h2"
                  sx={{ m: '8px 0 0', fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, letterSpacing: '-.02em', color: 'var(--tl-g9)' }}
                >
                  Where should we come?
                </Typography>

                {/* Google search + GPS, filling the three fields below.
                    Renders nothing if Maps cannot load, so the manual
                    fields are never left broken. */}
                <AddressAutofill onPick={applyPickedAddress} />

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                  <Box component="label" sx={{ ...fieldLabel, gridColumn: '1 / -1' }}>
                    House / flat, street
                    <Box
                      component="input"
                      value={patient.street}
                      placeholder="Flat 302, Sai Residency, Road No. 12"
                      onChange={(e) => setPatient((pt) => ({ ...pt, street: e.target.value }))}
                      sx={field}
                    />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Area
                    <Box
                      component="input"
                      value={patient.area}
                      placeholder="Banjara Hills"
                      onChange={(e) => setPatient((pt) => ({ ...pt, area: e.target.value }))}
                      sx={field}
                    />
                  </Box>
                  <Box component="label" sx={fieldLabel}>
                    Pincode
                    <Box
                      component="input"
                      value={patient.pincode}
                      inputMode="numeric"
                      maxLength={6}
                      onChange={(e) => setPatient((pt) => ({ ...pt, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      sx={field}
                    />
                  </Box>
                </Box>

                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: pinColour }}>
                  {pinMessage}
                </Box>

                <Box
                  sx={{
                    display: 'flex', flexDirection: 'column', gap: 0.75,
                    p: '14px 16px', borderRadius: '18px', border: '1.5px dashed var(--tl-line)',
                  }}
                >
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>
                    Link your ABHA{' '}
                    <Box component="span" sx={{ fontWeight: 600, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                      (optional)
                    </Box>
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                    Add your Ayushman Bharat Health Account number and your report is saved to
                    your national health record.
                  </Box>
                  <Box
                    component="input"
                    value={patient.abha}
                    placeholder="14-digit ABHA number or ABHA address"
                    aria-label="ABHA number or address"
                    onChange={(e) => setPatient((pt) => ({ ...pt, abha: e.target.value }))}
                    sx={{ ...field, fontSize: 15, p: '12px 14px', bgcolor: '#FFFFFF' }}
                  />
                </Box>
              </Box>
            )}

            {/* 2 — time slot */}
            {step === 2 && (
              <Box component="section" sx={card}>
                <Typography component="h1" sx={stepTitle}>{HEADINGS[2]}</Typography>

                <Box
                  sx={{
                    display: 'flex', gap: 1, pb: 0.5,
                    overflowX: 'auto', scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {days.map((d) => {
                    const on = d.key === date;
                    return (
                      <Box
                        key={d.key}
                        component="button"
                        type="button"
                        onClick={() => setDate(d.key)}
                        aria-pressed={on}
                        sx={{
                          flexShrink: 0, minWidth: 84, cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                          p: '12px 16px', borderRadius: '18px',
                          border: `1.5px solid ${on ? 'var(--tl-g9)' : 'var(--tl-line)'}`,
                          bgcolor: on ? 'var(--tl-g9)' : '#FFFFFF',
                          color: on ? '#FFFFFF' : 'var(--tl-g9)',
                        }}
                      >
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 700, opacity: 0.8 }}>
                          {d.day}
                        </Box>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 20, fontWeight: 800 }}>{d.date}</Box>
                      </Box>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    p: '12px 16px', borderRadius: '16px', bgcolor: 'var(--tl-mint)',
                    fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-muted)',
                  }}
                >
                  <Box component="strong" sx={{ color: 'var(--tl-g9)' }}>Same-day report, guaranteed*</Box>{' '}
                  for most routine tests when the sample is collected before 12 noon. *Conditions apply.
                </Box>

                {needsFasting && (
                  <Box
                    sx={{
                      p: '14px 16px', borderRadius: '16px', bgcolor: '#FFF7A8',
                      fontFamily: UI, fontSize: 14, fontWeight: 700, lineHeight: 1.5, color: 'var(--tl-g9)',
                    }}
                  >
                    Your tests need fasting. We&rsquo;ve highlighted early-morning slots so you
                    don&rsquo;t wait long to eat.
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 130px), 1fr))', gap: 1 }}>
                  {SLOTS.map((t, i) => {
                    const on = t === slot;
                    const early = needsFasting && i < 3;
                    const late = i >= 5;
                    const sub = early ? 'Best for fasting' : late ? 'Report next morning' : 'Same-day report';
                    return (
                      <Box
                        key={t}
                        component="button"
                        type="button"
                        onClick={() => setSlot(t)}
                        aria-pressed={on}
                        sx={{
                          cursor: 'pointer', display: 'flex', flexDirection: 'column',
                          alignItems: 'flex-start', gap: '2px',
                          p: '12px 10px', borderRadius: '14px',
                          border: `1.5px solid ${on ? 'var(--tl-teal)' : early ? '#E8D800' : 'var(--tl-line)'}`,
                          bgcolor: on ? 'var(--tl-teal)' : early ? '#FFFCE0' : '#FFFFFF',
                          color: on ? '#FFFFFF' : 'var(--tl-g9)',
                        }}
                      >
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>{t}</Box>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 700, opacity: 0.75 }}>
                          {sub}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* 3 — the preparation checklist. Kept from the old flow on
                   purpose; the reference has no equivalent step. */}
            {step === 3 && (
              <Box sx={{ animation: `tl-stgin .45s ${EASE} both` }}>
                <PrepChecklist
                  acknowledged={prepAcknowledged}
                  onAcknowledgeChange={setPrepAcknowledged}
                />
              </Box>
            )}

            {/* 4 — payment */}
            {step === 4 && (
              <Box component="section" sx={card}>
                <Typography component="h1" sx={stepTitle}>{HEADINGS[4]}</Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[['payu', 'Pay online', 'UPI, cards, net banking and wallets — secure 3DS']].map(([v, t, d]) => {
                    const on = pay === v;
                    return (
                      <Box
                        key={v}
                        component="button"
                        type="button"
                        onClick={() => setPay(v)}
                        aria-pressed={on}
                        sx={{
                          cursor: 'pointer', textAlign: 'left', color: 'var(--tl-g9)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5,
                          p: '16px 18px', borderRadius: '18px',
                          border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                          bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>{t}</Box>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>{d}</Box>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'grid', placeItems: 'center', flexShrink: 0,
                            width: 22, height: 22, borderRadius: '50%',
                            border: `2px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                          }}
                        >
                          <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: on ? 'var(--tl-teal)' : 'transparent' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1.5px solid #E1ECE8', borderRadius: '18px', overflow: 'hidden', fontFamily: UI, fontSize: 14 }}>
                  <Box component="span" sx={{ p: '12px 16px', bgcolor: 'var(--tl-mint)', fontWeight: 800 }}>
                    Cancellation and refunds
                  </Box>
                  {CANCELLATION.map(([l, v, good]) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, p: '11px 16px', borderTop: '1px solid #E1ECE8' }}>
                      <Box component="span">{l}</Box>
                      <Box component="span" sx={{ fontWeight: 800, textAlign: 'right', color: good ? 'var(--tl-teal-d)' : 'inherit' }}>
                        {v}
                      </Box>
                    </Box>
                  ))}
                  <Box component="span" sx={{ p: '10px 16px', borderTop: '1px solid #E1ECE8', fontSize: 12, color: 'var(--tl-muted-2)' }}>
                    Refunds reach your original payment method within 7 business days.
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '16px 18px', borderRadius: '18px', bgcolor: '#F6FAF8', fontFamily: UI, fontSize: 14 }}>
                  {[
                    ['Patient', patient.name || '—'],
                    ['Visit', slotSummary],
                    ['Address', [patient.area, patient.pincode].filter(Boolean).join(' · ') || '—'],
                  ].map(([l, v]) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                      <Box component="span" sx={{ color: 'var(--tl-muted-2)' }}>{l}</Box>
                      <Box component="span" sx={{ fontWeight: 800, textAlign: 'right', minWidth: 0 }}>{v}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── back / next ──────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                sx={{
                  border: 0, cursor: 'pointer', borderRadius: '16px', p: '16px 22px',
                  bgcolor: '#FFFFFF', color: 'var(--tl-g9)',
                  fontFamily: UI, fontSize: 16, fontWeight: 800,
                  visibility: step > 0 ? 'visible' : 'hidden',
                }}
              >
                ← Back
              </Box>
              <Box
                component="button"
                type="button"
                onClick={next}
                disabled={busy}
                sx={{
                  flex: 1, maxWidth: 360, border: 0,
                  cursor: canContinue() && !busy ? 'pointer' : 'default',
                  borderRadius: '16px', p: '16px 28px',
                  bgcolor: canContinue() && !busy ? 'var(--tl-g9)' : '#B9C9C4',
                  color: '#FFFFFF', fontFamily: UI, fontSize: 16, fontWeight: 800,
                  transition: `background-color .25s ${EASE}`,
                }}
              >
                {busy
                  ? 'Placing your booking…'
                  : step === STEPS.length - 1
                    ? `Pay ₹ ${grand.toLocaleString('en-IN')} & confirm`
                    : 'Continue →'}
              </Box>
            </Box>
          </Box>

          {/* ── your visit ───────────────────────────────────────────── */}
          <Box
            component="aside"
            sx={{
              flex: '1 1 300px', minWidth: 0,
              display: 'flex', flexDirection: 'column', gap: 1.75,
              p: 3, borderRadius: '28px',
              bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
              position: { md: 'sticky' }, top: { md: 16 },
            }}
          >
            <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22 }}>Your visit</Box>

            {items.length === 0 ? (
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: '#9FC2BA' }}>
                No tests added yet.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {items.map((c) => (
                  <Box
                    key={`sum-${c.type}-${c.id}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 1.25, borderTop: '1px solid var(--tl-g6)', fontFamily: UI, fontSize: 15 }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, minWidth: 0 }}>{c.name}</Box>
                    <Box component="span" sx={{ color: 'var(--tl-line-2)', whiteSpace: 'nowrap' }}>
                      {Number(c.price) > 0 ? `₹ ${Number(c.price).toLocaleString('en-IN')}` : '—'}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid var(--tl-g6)', fontFamily: UI, fontSize: 14 }}>
              <Box component="span" sx={asideRow}>
                <Box component="span" sx={{ color: '#9FC2BA' }}>Tests</Box>
                <Box component="span" sx={{ fontWeight: 800 }}>₹ {grand.toLocaleString('en-IN')}</Box>
              </Box>
              {[
                ['Home collection', 'Free'],
                ['Doctor review', 'Included'],
                ['Doctor call if flagged', 'Free'],
              ].map(([l, v]) => (
                <Box key={l} component="span" sx={asideRow}>
                  <Box component="span" sx={{ color: '#9FC2BA' }}>{l}</Box>
                  <Box component="span" sx={{ fontWeight: 800, color: 'var(--tl-teal-l)' }}>{v}</Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1.75, borderTop: '1px solid var(--tl-g6)' }}>
              <Box component="span" sx={{ fontFamily: UI, fontWeight: 800 }}>Total</Box>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontSize: 30, fontWeight: 700 }}>
                ₹ {grand.toLocaleString('en-IN')}
              </Box>
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-line-2)' }}>
              No hidden charges. This is the full amount you pay.
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1.75, borderTop: '1px solid var(--tl-g6)' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {PAY_BADGES.map((b) => (
                  <Box
                    key={b}
                    component="span"
                    sx={{ p: '6px 9px', borderRadius: '8px', bgcolor: 'var(--tl-g7)', fontFamily: UI, fontSize: 12, fontWeight: 800 }}
                  >
                    {b}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontFamily: UI, fontSize: 13, color: 'var(--tl-line-2)' }}>
                {ASSURANCES.map((a) => (
                  <Box key={a} component="span" sx={{ display: 'flex', gap: 1 }}>
                    <Box component="span" sx={{ color: 'var(--tl-teal-l)', fontWeight: 800 }}>✓</Box>
                    {a}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Booking;
